import supabase from '../lib/supabaseClient';

const normaliseToArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return [];
};

export const fetchKnowledgeEntries = async (userId) => {
  if (!userId) {
    return [];
  }

  const { data, error } = await supabase
    .from('knowledge_entries')
    .select(
      `id,
       user_id,
       title,
       summary,
       content,
       category,
       tags,
       difficulty,
       read_minutes,
       source,
       related_habits,
       favorite,
       type,
       updated_at,
       created_at,
       last_modified`
    )
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return (data ?? []).map((entry) => ({
    id: entry.id,
    title: entry.title ?? 'Untitled note',
    summary: entry.summary ?? '',
    category: entry.category ?? 'General',
    tags: normaliseToArray(entry.tags),
    difficulty: entry.difficulty ?? 'Quick skim',
    readMinutes: entry.read_minutes ?? 0,
    source: entry.source ?? null,
    type: entry.type ?? 'note',
    relatedHabits: normaliseToArray(entry.related_habits),
    favorite: Boolean(entry.favorite),
    lastModified: entry.updated_at ?? entry.last_modified ?? entry.created_at,
    createdAt: entry.created_at,
    content: entry.content ?? '',
  }));
};

export const updateKnowledgeFavorite = async ({ entryId, userId, favorite }) => {
  const { data, error } = await supabase
    .from('knowledge_entries')
    .update({ favorite })
    .eq('id', entryId)
    .eq('user_id', userId)
    .select('id, favorite')
    .single();

  if (error) {
    throw error;
  }

  return data;
};

const sanitiseArrayField = (value) => {
  if (Array.isArray(value)) {
    return value.filter((entry) => Boolean(entry) && typeof entry === 'string');
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return [];
};

export const createKnowledgeEntry = async ({
  userId,
  title,
  summary,
  content,
  category,
  tags,
  difficulty,
  readMinutes,
  source,
  relatedHabits,
  type = 'note'
}) => {
  if (!userId) {
    throw new Error('User ID is required to create a knowledge entry.');
  }

  const payload = {
    user_id: userId,
    title: title?.trim() || 'Untitled note',
    summary: summary?.trim() || null,
    content: content?.trim() || null,
    category: category?.trim() || 'General',
    tags: sanitiseArrayField(tags),
    difficulty: difficulty?.trim() || 'Quick skim',
    read_minutes: Number.isFinite(readMinutes) ? Math.max(0, Math.round(readMinutes)) : null,
    source: source?.trim() || null,
    related_habits: sanitiseArrayField(relatedHabits),
    type,
    favorite: false
  };

  const { data, error } = await supabase
    .from('knowledge_entries')
    .insert(payload)
    .select(
      'id, user_id, title, summary, content, category, tags, difficulty, read_minutes, source, related_habits, favorite, type, updated_at, created_at'
    )
    .single();

  if (error) {
    throw error;
  }

  return {
    id: data.id,
    title: data.title,
    summary: data.summary,
    category: data.category,
    tags: data.tags ?? [],
    difficulty: data.difficulty,
    readMinutes: data.read_minutes ?? 0,
    source: data.source,
    relatedHabits: data.related_habits ?? [],
    favorite: Boolean(data.favorite),
    type: data.type,
    lastModified: data.updated_at ?? data.created_at,
    createdAt: data.created_at,
    content: data.content ?? ''
  };
};
